/**
 * Step2AboutYou Component
 *
 * Collects:
 * - Full legal name (text input)
 * - Date of birth (date input)
 * - Mailing address (street, unit optional, city, state dropdown, ZIP)
 * - Phone on file (text input with format validation)
 * - Is phone current? (checkbox toggle)
 * - If not current: new phone number (conditional text input)
 *
 * Uses react-hook-form's useFormContext and Controller for managed state.
 * Validates against step2Schema from formSchemas.
 */

import { useFormContext, Controller } from 'react-hook-form';
import { Emery } from '@/components/ui/Calla';
import { EMERY_MESSAGES } from '@/constants/callaMessages';
import { FormField } from '@/components/ui/FormField';
import { DateOfBirthInput } from '@/components/ui/DateOfBirthInput';
import { SearchableSelect } from '@/components/ui/SearchableSelect';
import { US_STATES } from '@/constants/states';
import { type FullFormData } from '@/schemas/formSchemas';

export default function Step2AboutYou() {
  const { control, formState: { errors } } = useFormContext<FullFormData>();

  return (
    <div>
      <Emery message={EMERY_MESSAGES.step2} />

      {/* Full Legal Name */}
      <FormField
        label="Full Legal Name"
        name="step2.fullLegalName"
        required={true}
        error={errors.step2?.fullLegalName?.message}
        helperText="As it appears on your policy"
      >
        <Controller
          name="step2.fullLegalName"
          control={control}
          render={({ field }) => (
            <input
              {...field}
              type="text"
              autoComplete="name"
              enterKeyHint="next"
              autoCapitalize="words"
              data-sensitive=""
              placeholder="First Middle Last"
              className={`form-input ${
                errors.step2?.fullLegalName ? 'form-input-error' : ''
              }`}
            />
          )}
        />
      </FormField>

      {/* Date of Birth */}
      <FormField
        label="Date of Birth"
        name="step2.dateOfBirth"
        required={true}
        sensitive={true}
        error={errors.step2?.dateOfBirth?.message}
        whyText="Your age is used to calculate your premium and verify identity."
      >
        <Controller
          name="step2.dateOfBirth"
          control={control}
          render={({ field }) => (
            <DateOfBirthInput value={field.value} onChange={field.onChange} error={!!errors.step2?.dateOfBirth} />
          )}
        />
      </FormField>

      <h3 className="field-group-heading">Your mailing address</h3>

      {/* Mailing Address - Street */}
      <FormField
        label="Street Address"
        name="step2.mailingAddressStreet"
        required={true}
        error={errors.step2?.mailingAddressStreet?.message}
      >
        <Controller
          name="step2.mailingAddressStreet"
          control={control}
          render={({ field }) => (
            <input
              {...field}
              type="text"
              autoComplete="street-address"
              enterKeyHint="next"
              autoCapitalize="words"
              data-sensitive=""
              placeholder="123 Main St"
              className={`form-input ${
                errors.step2?.mailingAddressStreet ? 'form-input-error' : ''
              }`}
            />
          )}
        />
      </FormField>

      {/* Mailing Address - Unit */}
      <FormField
        label="Unit / Suite / Apt (Optional)"
        name="step2.mailingAddressUnit"
        required={false}
        error={errors.step2?.mailingAddressUnit?.message}
      >
        <Controller
          name="step2.mailingAddressUnit"
          control={control}
          render={({ field }) => (
            <input
              {...field}
              type="text"
              autoComplete="address-line2"
              enterKeyHint="next"
              placeholder="Apt 2B"
              className="form-input"
            />
          )}
        />
      </FormField>

      {/* Mailing Address - City */}
      <FormField
        label="City"
        name="step2.mailingAddressCity"
        required={true}
        error={errors.step2?.mailingAddressCity?.message}
      >
        <Controller
          name="step2.mailingAddressCity"
          control={control}
          render={({ field }) => (
            <input
              {...field}
              type="text"
              autoComplete="address-level2"
              enterKeyHint="next"
              autoCapitalize="words"
              placeholder="Anytown"
              className={`form-input ${
                errors.step2?.mailingAddressCity ? 'form-input-error' : ''
              }`}
            />
          )}
        />
      </FormField>

      {/* Mailing Address - State */}
      <FormField
        label="State"
        name="step2.mailingAddressState"
        required={true}
        error={errors.step2?.mailingAddressState?.message}
      >
        <Controller
          name="step2.mailingAddressState"
          control={control}
          render={({ field }) => (
            <SearchableSelect
              options={US_STATES.map((s) => ({ value: s.code, label: s.name }))}
              value={field.value}
              onChange={field.onChange}
              placeholder="Search or select state"
              error={!!errors.step2?.mailingAddressState}
            />
          )}
        />
      </FormField>

      {/* Mailing Address - ZIP */}
      <FormField
        label="ZIP Code"
        name="step2.mailingAddressZip"
        required={true}
        error={errors.step2?.mailingAddressZip?.message}
        helperText="5-digit ZIP code"
      >
        <Controller
          name="step2.mailingAddressZip"
          control={control}
          render={({ field }) => (
            <input
              {...field}
              type="text"
              inputMode="numeric"
              autoComplete="postal-code"
              enterKeyHint="next"
              maxLength={5}
              pattern="[0-9]{5}"
              placeholder="12345"
              className={`form-input ${
                errors.step2?.mailingAddressZip ? 'form-input-error' : ''
              }`}
            />
          )}
        />
      </FormField>

      {/* Home Ownership */}
      <FormField
        label="Do you own or rent at this address?"
        name="step2.homeOwnership"
        required={true}
        error={errors.step2?.homeOwnership?.message}
      >
        <Controller
          name="step2.homeOwnership"
          control={control}
          render={({ field }) => (
            <select
              {...field}
              className={`form-input ${
                errors.step2?.homeOwnership ? 'form-input-error' : ''
              }`}
            >
              <option value="">Select an option</option>
              <option value="own">Own</option>
              <option value="rent">Rent</option>
              <option value="other">Other</option>
            </select>
          )}
        />
      </FormField>

      {/* Phone (current, required) */}
      <FormField
        label="Your current phone number"
        name="step2.phoneOnFile"
        required={true}
        error={errors.step2?.phoneOnFile?.message}
        helperText="If this is different from what's on your policy, that's OK. We'll update it."
        whyText="We use this to verify your account and contact you about your quote."
      >
        <Controller
          name="step2.phoneOnFile"
          control={control}
          render={({ field }) => (
            <input
              {...field}
              type="tel"
              inputMode="tel"
              autoComplete="tel-national"
              enterKeyHint="next"
              data-sensitive=""
              placeholder="10-digit phone number"
              className={`form-input ${
                errors.step2?.phoneOnFile ? 'form-input-error' : ''
              }`}
            />
          )}
        />
      </FormField>
    </div>
  );
}
